import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { stripeApi } from "../services/stripe";
import { usersApi } from "../services/users";
import { masspayApi } from "../services/masspay";
import { payoutCountryApi } from "../services/payoutCountry";
import { countriesApi } from "../services/countries";
import { closeMoneyGate, onMoneyGate, openReviewSheet, GateProfile } from "../lib/connectBank";
import { openStripeUrl, stripeClientFields, onStripeBrowserClosed } from "../lib/stripeFlow";
import { onIdCheckReturn, openIdCheck, reserveIdCheckTab } from "../lib/idCheckFlow";
import { FALLBACK_COUNTRIES, fetchCountryData, SupportedCountry } from "../lib/supportedCountries";
import type { User } from "../types/User";
import type { PayoutInfo } from "../types/payout";
import { ConnectBody, ConnectScreen } from "./connect/ConnectScreen";
import CountryPicker from "./connect/CountryPicker";
import { MasspayRail, NotYetRail, StripeRail } from "./connect/RailScreens";
import { ChangeCountryFace, MasspayFace, masspayFace, MpElsewhere, MpHold, MpName, MpRetry, MpReview, MpWaiting } from "./MasspayFaces";
import "./ConnectBankSheet.css";
import { t } from "../i18n";

// Free tier: "Connect your bank", behind every money button (lib/connectBank).
// Full screen since 1.5.2 (founder, 2026-10-19 — .claude/1.5.2-connect-bank-fullscreen.html):
//
//   country   only for a talent with NO payout country on file (they skipped the
//             bank at signup, or changed country): their location pre-selected,
//             popular countries, every country A–Z, and search. Never guessed
//             for them — they tap it (or Continue on the selected one).
//   stripe    "You'll be paid with" + the Stripe card → Stripe's own set-up
//   mpAsk     MassPay countries: "{country} selected" + the wallets → their
//             name as on the ID → MassPay's ID check (Veriff) → "You're in review"
//             (the steps in MasspayFaces)
//   notYet    a country we can't pay yet: Notify me (its waitlist), or another one
// Both rails: "Change" / "Not in Indonesia? Change country" while nothing is
// verified or earned (server lib/payoutCountry.js), so a wrong pick is never a trap.
type Face = "loading" | "country" | "stripe" | "notYet" | "changeCountry" | MasspayFace;

// How far along the set-up is, for the step bar.
const PROGRESS: Record<Face, number> = {
  loading: 25, country: 25, stripe: 50, mpAsk: 50, notYet: 50, changeCountry: 50,
  mpName: 75, mpElsewhere: 75, mpWaiting: 100, mpReview: 100, mpRetry: 100, mpHold: 100,
};

// The same rule as the server's (lib/masspay/connect.js): letters in any
// script, spaces, hyphens, apostrophes and dots; 1–50 characters.
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M} .'-]{0,49}$/u;

// Server refusals that only support can sort out.
const SUPPORT_CODES = new Set(["profile_change_needs_support", "needs_support"]);

const masspayNow = (p: GateProfile) => (p?.payout?.rail === "masspay" ? masspayFace(p.payout.masspay) : null);

// The face a money button's profile opens on, before the fresh read: MassPay's
// step, the Stripe screen for a saved country, the picker for none — or a
// moment of nothing when the profile doesn't say (a stale button).
function firstFace(p: GateProfile): Face {
  const mp = masspayNow(p);
  if (mp && mp !== "verified") return mp;
  if (p?.payout) return p.payout.country ? "stripe" : "country";
  return "loading";
}

export default function ConnectBankSheet() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  // Bumped on every open, so a second open while already open re-reads too
  // (a double tap on a money button must never leave the screen loading).
  const [session, setSession] = useState(0);
  const [face, setFaceState] = useState<Face>("loading");
  // Where Back goes from the name and change-country faces.
  const [backFace, setBackFace] = useState<Face>("mpAsk");
  const [profile, setProfile] = useState<User | null>(null);
  const [hasCountry, setHasCountry] = useState<boolean | null>(null);
  const [countries, setCountries] = useState<SupportedCountry[]>(FALLBACK_COUNTRIES);
  // Where the talent's connection is (server, never stored): the picker's suggestion.
  const [detected, setDetected] = useState<string | null>(null);
  const [country, setCountry] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [support, setSupport] = useState(false);
  const [note, setNote] = useState("");
  // Server location_mismatch: the country the connection is in (a Stripe one).
  const [elsewhere, setElsewhere] = useState("");
  const faceRef = useRef<Face>("loading");
  // Set once the talent moves inside the screen: a late server read must never
  // pull them back to another face.
  const touchedRef = useRef(false);
  const stopReturnRef = useRef<() => void>(() => {});

  const setFace = (f: Face) => {
    faceRef.current = f;
    setFaceState(f);
  };
  const go = (f: Face) => {
    touchedRef.current = true;
    setError("");
    setSupport(false);
    setNote("");
    setFace(f);
  };

  useEffect(() => onMoneyGate((gate, gateProfile) => {
    const next = gate === "bank";
    setOpen(next);
    stopReturnRef.current();
    stopReturnRef.current = () => {};
    if (!next) return;
    setSession((n) => n + 1);
    touchedRef.current = false;
    setError(""); setSupport(false); setNote(""); setPending(false);
    setFirst(""); setLast(""); setCountry("");
    setProfile(null); setHasCountry(null);
    setBackFace("mpAsk");
    // The profile the money button handed over picks the face at once (no
    // Stripe screen flashing before MassPay's); the fresh read below confirms it.
    setFace(firstFace(gateProfile));
  }), []);

  // Leaving the screen mid-check: stop listening for the ID check's return.
  useEffect(() => {
    const stopReturn = stopReturnRef;
    return () => stopReturn.current();
  }, []);

  // Publish a fresh profile to every open screen (the price card's line, the
  // QR card, the Profile page) after the payout side moved.
  const syncProfile = async (): Promise<User | null> => {
    if (!user?.slug) return null;
    try {
      const p = await usersApi.get(user.slug);
      setProfile(p);
      setHasCountry(!!p?.bankCountry);
      window.dispatchEvent(new CustomEvent("profile:patched", { detail: { payout: p?.payout ?? null, bankCountry: p?.bankCountry ?? "" } }));
      return p;
    } catch {
      return null;
    }
  };

  // Where a MassPay set-up stands → the face. Done → the review sheet takes over.
  const routeMasspay = (next: MasspayFace | "verified") => {
    if (next === "verified") {
      openReviewSheet();
      return;
    }
    setFace(next);
  };

  // On open: the talent's own profile (bank country, payout set-up) and the
  // country list. MassPay's webhooks are best-effort, so a set-up that isn't
  // done yet is re-read from MassPay itself (server-throttled).
  useEffect(() => {
    if (!open || !user?.slug) return;
    let alive = true;
    fetchCountryData({ fresh: true }).then((d) => {
      if (!alive) return;
      if (d.countries.length) setCountries(d.countries);
      setDetected(d.detectedCode);
    }).catch(() => {});
    usersApi.get(user.slug).then(async (p) => {
      if (!alive) return;
      setProfile(p);
      setHasCountry(!!p?.bankCountry);
      const payout = p?.payout;
      if (payout?.rail !== "masspay") {
        // Stripe (or an older server, where every saved country was Stripe's):
        // the Stripe screen, or the picker when no country is saved.
        if (!touchedRef.current) setFace(p?.bankCountry ? "stripe" : "country");
        return;
      }
      let m = payout.masspay;
      if (m && m.kycStatus !== "approved") {
        const s = await masspayApi.status().catch(() => null);
        if (!alive) return;
        if (s && (s.kycStatus !== m.kycStatus || s.payeeStatus !== m.status)) {
          m = { kycStatus: s.kycStatus, status: s.payeeStatus };
          const fresh: PayoutInfo = { ...payout, masspay: m };
          setProfile({ ...p, payout: fresh });
          window.dispatchEvent(new CustomEvent("profile:patched", { detail: { payout: fresh, bankCountry: p.bankCountry ?? "" } }));
        }
      }
      if (!touchedRef.current) routeMasspay(masspayFace(m));
    }).catch(() => {
      // A failed read keeps today's behaviour: the server uses what it has.
      if (!alive) return;
      setHasCountry(true);
      if (!touchedRef.current && faceRef.current === "loading") setFace("stripe");
    });
    return () => { alive = false; };
  }, [open, session, user?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const close = () => { if (!pending) closeMoneyGate(); };
  const picked = countries.find((c) => c.code === country) || null;
  const payout = profile?.payout || null;
  // A country picked on this screen (none saved yet) vs the saved one.
  const justPicked = !hasCountry && !!picked;
  const savedName = payout?.country || profile?.bankCountry || "";
  const savedCountry = savedName ? countries.find((c) => c.name === savedName) || null : null;
  const mpCountry = justPicked && picked!.rail === "masspay" ? picked : savedCountry;
  const mpCountryName = mpCountry?.name || payout?.country || "";
  const canChangeCountry = !!payout?.canChangeCountry && !!payout?.country;

  // ── Stripe ────────────────────────────────────────────────────────────
  const connect = async (iso?: string) => {
    setPending(true);
    setError("");
    try {
      const data = await stripeApi.connect({ ...(iso ? { country: iso } : {}), ...stripeClientFields() });
      if (!data?.url) throw new Error("no_url");
      // Native: Stripe opens in the in-app sheet over this page. If the talent
      // closes it before the return trip fires, un-stick the button.
      const stop = onStripeBrowserClosed(() => { stop(); setPending(false); });
      await openStripeUrl(data.url);
    } catch (e: any) {
      setError(e?.response?.data?.msg || t("connectBankSheet.couldnTReachStripe"));
      setPending(false);
    }
  };

  // A country that isn't open yet: Notify me puts the talent on its waitlist
  // with their account email (the screen says we'll email them), then closes.
  const joinWaitlist = async (c: SupportedCountry) => {
    const email = (user?.email || "").trim();
    if (!email) { closeMoneyGate(); return; }
    setPending(true);
    setError("");
    try {
      await countriesApi.joinWaitlist(email, c.name);
      setPending(false);
      closeMoneyGate();
    } catch {
      setError(t("connectBankSheet.couldnTSaveThat"));
      setPending(false);
    }
  };

  // The picker: one tap on a country moves on to who pays them.
  const pickCountry = (c: SupportedCountry) => {
    setCountry(c.code);
    if (!c.open) return go("notYet");
    if (c.rail === "masspay") {
      setBackFace("mpAsk");
      return go("mpAsk");
    }
    go("stripe");
  };

  // ── MassPay ───────────────────────────────────────────────────────────
  // Ask MassPay (through the server) where the ID check stands.
  const checkStatus = async () => {
    setPending(true);
    setError("");
    setNote("");
    try {
      const s = await masspayApi.status();
      const next = masspayFace({ kycStatus: s.kycStatus, status: s.payeeStatus });
      await syncProfile();
      // Back before MassPay has the check (or closed it early): stay on
      // "Finish your ID check", and say so.
      if (faceRef.current === "mpWaiting" && next === "mpAsk") {
        setNote(t("connectBankSheet.notFinishedYet"));
        return;
      }
      routeMasspay(next);
    } catch {
      setError(t("connectBankSheet.couldnTReachMasspay"));
    } finally {
      setPending(false);
    }
  };

  const onConnectError = (e: any) => {
    const code = e?.response?.data?.code;
    if (code === "masspay_on_hold") return go("mpHold");
    // Connecting from a country Stripe pays: stopped before MassPay's paid ID check.
    if (code === "location_mismatch") {
      setElsewhere(String(e.response.data.detectedCountry || ""));
      return go("mpElsewhere");
    }
    if (SUPPORT_CODES.has(code)) {
      setError(t("connectBankSheet.needsSupportProfile"));
      setSupport(true);
      return;
    }
    if (code === "not_masspay_country") return setError(t("connectBankSheet.masspayNotOpen"));
    // One paid ID check per talent (server, 2026-10-18): the next one is support's call.
    if (code === "id_check_limit") {
      setError(t("connectBankSheet.youVeUsedYourId"));
      setSupport(true);
      return;
    }
    if (code === "id_checks_paused") return setError(t("connectBankSheet.idChecksArePaused"));
    if (e?.response?.status === 400 && !code) return setError(t("connectBankSheet.enterYourName"));
    if (!e?.response || ["masspay_unavailable", "veriff_unavailable", "masspay_error"].includes(code)) {
      return setError(t("connectBankSheet.couldnTReachMasspay"));
    }
    setError(e?.response?.data?.msg || t("connectBankSheet.couldnTReachMasspay"));
  };

  // Create the payee (once) and open MassPay's ID check.
  const startIdCheck = async () => {
    const legalFirstName = first.trim();
    const legalLastName = last.trim();
    if (!NAME_RE.test(legalFirstName) || !NAME_RE.test(legalLastName)) {
      setError(t("connectBankSheet.enterYourName"));
      return;
    }
    touchedRef.current = true;
    setPending(true);
    setError("");
    setSupport(false);
    setNote("");
    const tab = reserveIdCheckTab();
    try {
      const countryCode = justPicked && picked!.rail === "masspay" ? picked!.code : undefined;
      const ans = await masspayApi.connect({ legalFirstName, legalLastName, ...(countryCode ? { countryCode } : {}) });
      if (ans?.done) {
        tab?.close();
        await checkStatus();
        return;
      }
      if (!ans?.url) throw new Error("no_url");
      // The country (if just picked) is saved now: every screen hears it.
      if (countryCode) syncProfile();
      stopReturnRef.current();
      const stop = onIdCheckReturn(() => {
        stop();
        stopReturnRef.current = () => {};
        checkStatus();
      });
      stopReturnRef.current = stop;
      await openIdCheck(ans.url, tab);
      // Under the in-app browser (native) or beside the new tab (web): what
      // the talent sees when they come back. Never a spinner they can't leave.
      setPending(false);
      setFace("mpWaiting");
    } catch (e: any) {
      tab?.close();
      setPending(false);
      onConnectError(e);
    }
  };

  // ── Change country (both rails) ───────────────────────────────────────
  const askChangeCountry = () => {
    setBackFace(faceRef.current);
    go("changeCountry");
  };

  const changeCountry = async () => {
    setPending(true);
    setError("");
    setSupport(false);
    try {
      await payoutCountryApi.reset();
      await syncProfile();
      setHasCountry(false);
      setCountry("");
      go("country");
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (SUPPORT_CODES.has(code)) {
        setError(t("connectBankSheet.contactSupportToChange"));
        setSupport(true);
      } else if (!e?.response || code === "try_again") {
        setError(t("connectBankSheet.couldnTChangeCountry"));
      } else {
        setError(e?.response?.data?.msg || t("connectBankSheet.couldnTChangeCountry"));
      }
    } finally {
      setPending(false);
    }
  };

  // Back to the picker for a country picked here; "Change your country?" for a saved one.
  const changeHandler = justPicked ? () => go("country") : canChangeCountry ? askChangeCountry : undefined;

  let body: React.ReactNode;
  let onBack: (() => void) | undefined;
  switch (face) {
    case "loading":
      body = <ConnectBody center><span className="cxs-wait" role="status" aria-label={t("connectBank.loading")} /></ConnectBody>;
      break;
    case "country":
      body = <CountryPicker countries={countries} selectedCode={country || detected} onPick={pickCountry} pending={pending} error={error} />;
      break;
    case "stripe": {
      const shown = justPicked ? picked : savedCountry || (savedName ? { name: savedName } : null);
      body = <StripeRail country={shown} onChange={changeHandler} onContinue={() => connect(justPicked ? picked!.code : undefined)} pending={pending} error={error} />;
      if (justPicked) onBack = () => go("country");
      break;
    }
    case "notYet":
      body = picked ? <NotYetRail country={picked} onNotify={() => joinWaitlist(picked)} onOther={() => go("country")} pending={pending} error={error} /> : null;
      onBack = () => go("country");
      break;
    case "changeCountry":
      body = <ChangeCountryFace pending={pending} error={error} support={support} onConfirm={changeCountry} onBack={() => go(backFace)} />;
      onBack = () => go(backFace);
      break;
    case "mpAsk":
      body = (
        <MasspayRail
          country={{ name: mpCountryName, code: mpCountry?.code, wallets: mpCountry?.wallets || [] }}
          onChange={changeHandler}
          onContinue={() => { setBackFace("mpAsk"); go("mpName"); }}
          pending={pending}
          error={error}
        />
      );
      if (justPicked) onBack = () => go("country");
      break;
    case "mpName":
      body = (
        <MpName
          first={first}
          last={last}
          onFirst={(v) => { setFirst(v); setError(""); }}
          onLast={(v) => { setLast(v); setError(""); }}
          pending={pending}
          error={error}
          support={support}
          onSubmit={startIdCheck}
        />
      );
      onBack = () => go(backFace);
      break;
    case "mpWaiting":
      body = <MpWaiting pending={pending} note={note} error={error} onDone={checkStatus} onReopen={startIdCheck} />;
      break;
    case "mpReview":
      body = <MpReview onClose={close} />;
      break;
    case "mpRetry":
      body = (
        <MpRetry
          country={mpCountryName}
          canChangeCountry={canChangeCountry}
          onRetry={() => { setBackFace("mpRetry"); go("mpName"); }}
          onChangeCountry={askChangeCountry}
        />
      );
      break;
    case "mpHold":
      body = <MpHold onClose={close} />;
      break;
    case "mpElsewhere":
      body = (
        <MpElsewhere
          detected={elsewhere}
          home={mpCountryName}
          // A saved country goes through "Change your country?"; one only picked here is just picked again.
          canChangeCountry={canChangeCountry || !hasCountry}
          onChangeCountry={() => (hasCountry ? askChangeCountry() : go("country"))}
          onClose={close}
        />
      );
      break;
  }

  return (
    <ConnectScreen progress={PROGRESS[face]} onClose={close} onBack={onBack} busy={pending}>
      {body}
    </ConnectScreen>
  );
}
